import { H5, RadioField, TextField } from "@dgs/core";
import { Field, FieldArray, FieldProps, useField, useFormikContext } from "formik";
import { FC, Fragment } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { UserResponsibilitySelect } from "../../../shared/events/eventUsers/UserResponsibilitySelect";
import {
	CostCategoryRequirement,
	EventTypeRequirement,
	UserResponsibilityRequirement,
} from "../../../shared/types/eventType";
import { CostCategoryMultiSelectField } from "../shared/select/CostCategoryMultiSelect";
import { eventTypeMetricRequirements, eventTypeStaticRequirements } from "../shared/types/eventType";

interface Props {
	showInfo: boolean;
}

const Info = styled.div`
	padding-top: ${({ theme }) => theme.spacing(2)};
	padding-bottom: ${({ theme }) => theme.spacing(6)};
`;

const RadioFieldGroupWrapper = styled.div`
	padding-bottom: ${({ theme }) => theme.spacing(6)};
	display: grid;
	grid-template-columns: repeat(4, 1fr);
`;

const Col = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	border-left: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	padding: 1rem;
	border-top: none;
`;

const FirstCol = styled.div`
	display: flex;
	padding: 1rem;
	border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
`;

const Text = styled.p`
	font-size: ${({ theme }) => theme.fonts.sizes.small};
`;

const CenteredText = styled.p`
	text-align: center;
	font-size: ${({ theme }) => theme.fonts.sizes.small};
`;

export const EventTypeSettingsFormFields: FC<Props> = ({ showInfo }) => {
	const { t } = useTranslation();
	const { setFieldValue } = useFormikContext();
	const [{ value: costCategoryRequirements }, , { setValue: setCostCategoryRequirements }] =
		useField<CostCategoryRequirement[]>("costCategoryRequirements");
	const [{ value: userResponsibilityRequirements }, , { setValue: setUserResponsibilityRequirements }] = useField<
		UserResponsibilityRequirement[]
	>("userResponsibilityRequirements");

	const setAllInGroup = (names: string[], value: EventTypeRequirement) =>
		names.forEach((name) => setFieldValue(name, value));

	return (
		<>
			{showInfo &&
				t(
					"Here you can specify which data is relevant for the event and which fields are required. Basic information is always requested and is therefore not listed.",
				)}
			<RadioFieldGroupWrapper>
				<EventTypeSettingsHeader onColumnClick={(value) => setAllInGroup([...eventTypeStaticRequirements], value)} />
				{eventTypeStaticRequirements.map((requirement) => (
					<Fragment key={requirement}>
						<FirstCol>
							<Text>{t(requirement)}</Text>
						</FirstCol>
						<RadioFields name={requirement} id={requirement} />
					</Fragment>
				))}
			</RadioFieldGroupWrapper>
			<UserResponsibilitySelect
				name="userResponsibilities"
				label={t("Responsibilities")}
				onChange={(e) =>
					setUserResponsibilityRequirements(
						(e.target.value ?? [])
							.map(
								(option): UserResponsibilityRequirement =>
									userResponsibilityRequirements.find(
										({ userResponsibility }) => userResponsibility.id === option.id,
									) ?? { userResponsibility: option, requirement: "optional" },
							)
							.sort((a, b) => a.userResponsibility.name.localeCompare(b.userResponsibility.name)),
					)
				}
			/>
			<FieldArray name="userResponsibilityRequirements">
				{(props) => (
					<RadioFieldGroupWrapper>
						{(props.form.values.userResponsibilityRequirements as UserResponsibilityRequirement[]).map(
							({ userResponsibility }, index) => (
								<Fragment key={`${index}`}>
									{index === 0 && (
										<EventTypeSettingsHeader
											onColumnClick={(value) =>
												setAllInGroup(
													userResponsibilityRequirements.map(
														(_, i) => `userResponsibilityRequirements[${i}][requirement]`,
													),
													value,
												)
											}
										/>
									)}
									<FirstCol>
										<Text>{userResponsibility.name}</Text>
									</FirstCol>
									<RadioFields
										name={`${props.name}[${index}][requirement]`}
										id={`${props.name}-${index}-${userResponsibility.id}`}
									/>
								</Fragment>
							),
						)}
					</RadioFieldGroupWrapper>
				)}
			</FieldArray>
			{showInfo && <Info>{t("Here you can specify which cost categories are relevant for this event type.")}</Info>}
			<CostCategoryMultiSelectField
				name="costCategories"
				label={t("Cost categories")}
				onChange={(e) =>
					setCostCategoryRequirements(
						(e.target.value ?? [])
							.map(
								(option): CostCategoryRequirement =>
									costCategoryRequirements.find(({ costCategory }) => costCategory.id === option.id) ?? {
										costCategory: option,
										requirement: "optional",
									},
							)
							.sort((a, b) => a.costCategory.name.localeCompare(b.costCategory.name)),
					)
				}
			/>
			<FieldArray name="costCategoryRequirements">
				{(props) => (
					<RadioFieldGroupWrapper>
						{(props.form.values.costCategoryRequirements as CostCategoryRequirement[]).map(
							({ costCategory }, index) => (
								<Fragment key={`${index}`}>
									{index === 0 && (
										<EventTypeSettingsHeader
											onColumnClick={(value) =>
												setAllInGroup(
													costCategoryRequirements.map((_, i) => `costCategoryRequirements[${i}][requirement]`),
													value,
												)
											}
										/>
									)}
									<FirstCol>
										<Text>{t(costCategory.name)}</Text>
									</FirstCol>
									<RadioFields
										name={`${props.name}[${index}][requirement]`}
										id={`${props.name}-${index}-${costCategory.id}`}
									/>
								</Fragment>
							),
						)}
					</RadioFieldGroupWrapper>
				)}
			</FieldArray>
			<H5>{t("Offboarding")}</H5>
			{showInfo &&
				t(
					"This is where you control whether an offboarding process is active for an event type and which validation logic applies to the associated input fields. Offboarding is only visible to project managers.",
				)}
			<RadioFieldGroupWrapper>
				<EventTypeSettingsHeader onColumnClick={(value) => setAllInGroup([...eventTypeMetricRequirements], value)} />
				{eventTypeMetricRequirements.map((requirement) => (
					<Fragment key={requirement}>
						<FirstCol>
							<Text>{t(requirement)}</Text>
						</FirstCol>
						<RadioFields name={requirement} id={requirement} />
					</Fragment>
				))}
			</RadioFieldGroupWrapper>
			<Field name="eventGoalRatingMetricRequirement">
				{(p: FieldProps<EventTypeRequirement>) =>
					p.field.value !== "prohibited" && (
						<TextField name="eventGoalRatingMetricWeight" type="number" label={t("Event goal rating weight")} />
					)
				}
			</Field>
		</>
	);
};

const ClickableCol = styled(Col)`
	cursor: pointer;

	&:hover {
		background-color: ${({ theme }) => theme.colors.palette.grey[10]};
	}
`;

const EventTypeSettingsHeader = ({ onColumnClick }: { onColumnClick: (value: EventTypeRequirement) => void }) => {
	const { t } = useTranslation();

	return (
		<>
			<FirstCol />
			<ClickableCol onClick={() => onColumnClick("required")}>
				<div>
					<CenteredText>{t("display")}</CenteredText>
					<CenteredText>({t("required")})</CenteredText>
				</div>
			</ClickableCol>
			<ClickableCol onClick={() => onColumnClick("optional")}>
				<div>
					<CenteredText>{t("display")}</CenteredText>
					<CenteredText>({t("optional")})</CenteredText>
				</div>
			</ClickableCol>
			<ClickableCol onClick={() => onColumnClick("prohibited")}>
				<CenteredText>{t("prohibited")}</CenteredText>
			</ClickableCol>
		</>
	);
};

const eventTypeRequirements: EventTypeRequirement[] = ["required", "optional", "prohibited"];

const RadioFields: FC<{ name: string; id: string }> = ({ name, id }) => {
	return eventTypeRequirements.map((value) => (
		<Col key={value}>
			<RadioField name={name} label="" value={value} id={id} />
		</Col>
	));
};
